import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent, DialogFooterComponent, DialogHeaderComponent, FormExtendedCheckboxComponent } from '@akostm/orbit-ui';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageTemplate, MessageTemplateKind, MessageTemplateParameter, ParameterKind } from '@clientwire/api';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';

export interface MessageTemplateDialogData {
  title?: string;
  template?: MessageTemplate;
  cancelLabel?: string;
  confirmLabel?: string;
  message?: string;
  isConfirmation?: boolean;
}

@Component({
  selector: 'lib-message-template-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DialogHeaderComponent,
    DialogFooterComponent,
    ButtonComponent,
    ReactiveFormsModule,
    TranslateModule,
    FontAwesomeModule,
    FormExtendedCheckboxComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './message-template-dialog.component.html',
  styleUrl: './message-template-dialog.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessageTemplateDialogComponent implements OnInit, AfterViewInit {
  readonly #dialogRef = inject(DialogRef);
  readonly #formBuilder = inject(FormBuilder);
  readonly #translateService = inject(TranslateService);

  // Icons
  readonly addIcon = faPlus;
  readonly deleteIcon = faTrash;

  readonly isEditMode = signal(false);
  readonly isConfirmation = signal(false);

  // Only allow English and German
  readonly availableLanguages = ['en', 'de'];

  // For value path prefix - updated with trailing dot
  readonly valuePathPrefix = '$.conversationData.';
  @ViewChild('valuePathInput') valuePathInput?: ElementRef<HTMLInputElement>;

  // All parameter types and filtered types
  readonly parameterTypes = Object.values(ParameterKind);
  filteredParameterTypes: ParameterKind[] = [];

  readonly data: MessageTemplateDialogData = inject(DIALOG_DATA);
  templateForm!: FormGroup;

  // System template names constants
  private readonly NOTIFY_PARTICIPANTS_TEMPLATE = 'NotifyParticipantsTemplate';
  private readonly WORKFLOW_ERROR_TEMPLATE = 'WorkflowErrorTemplate';

  // Template kinds
  readonly templateKinds = Object.values(MessageTemplateKind);

  ngOnInit(): void {
    this.isConfirmation.set(!!this.data.isConfirmation || !!this.data.message);

    if (!this.isConfirmation()) {
      this.isEditMode.set(!!this.data.template);

      const editModeValue = this.isEditMode();
      // Filter parameter types based on context
      this.filterParameterTypes();

      this.data.title =
        this.data.title ||
        this.#translateService.instant(
          editModeValue
            ? 'SETTINGS.MESSAGE_TEMPLATES.EDIT_TEMPLATE'
            : 'SETTINGS.MESSAGE_TEMPLATES.ADD_TEMPLATE'
        );

      this.data.cancelLabel =
        this.data.cancelLabel ||
        this.#translateService.instant('SHARED.DIALOGS.CONFIRM_DIALOG.CANCEL_LABEL');

      this.data.confirmLabel =
        this.data.confirmLabel ||
        this.#translateService.instant(
          editModeValue ? 'SETTINGS.MESSAGE_TEMPLATES.SAVE' : 'SETTINGS.MESSAGE_TEMPLATES.CREATE'
        );

      this.initForm();
    } else {
      this.data.cancelLabel =
        this.data.cancelLabel ||
        this.#translateService.instant('SHARED.DIALOGS.CONFIRM_DIALOG.CANCEL_LABEL');

      this.data.confirmLabel =
        this.data.confirmLabel ||
        this.#translateService.instant('SHARED.DIALOGS.CONFIRM_DIALOG.CONFIRM_LABEL');
    }
  }

  // Method to filter parameter types based on template context
  private filterParameterTypes(): void {
    // Define restricted types
    const restrictedTypes: ParameterKind[] = [
      ParameterKind.AppUrl,
      ParameterKind.Error,
      ParameterKind.WorkflowId,
    ];

    if (!this.isEditMode()) {
      // For new templates: exclude restricted types
      this.filteredParameterTypes = this.parameterTypes.filter(
        (type) => !restrictedTypes.some((rt) => rt === type)
      );
    } else if (this.data.template) {
      if (this.data.template.name === this.NOTIFY_PARTICIPANTS_TEMPLATE) {
        // Allow standard types plus APP_URL for NotifyParticipantsTemplate
        this.filteredParameterTypes = this.parameterTypes.filter(
          (type) => !restrictedTypes.some((rt) => rt === type) || type === ParameterKind.AppUrl
        );
      } else if (this.data.template.name === this.WORKFLOW_ERROR_TEMPLATE) {
        // Allow standard types plus ERROR and WORKFLOW_ID for WorkflowErrorTemplate
        this.filteredParameterTypes = this.parameterTypes.filter(
          (type) =>
            !restrictedTypes.some((rt) => rt === type) ||
            type === ParameterKind.Error ||
            type === ParameterKind.WorkflowId
        );
      } else {
        // For other existing templates: exclude restricted types
        this.filteredParameterTypes = this.parameterTypes.filter(
          (type) => !restrictedTypes.some((rt) => rt === type)
        );
      }
    } else {
      // Fallback: use all types if something goes wrong
      this.filteredParameterTypes = this.parameterTypes;
    }
  }

  ngAfterViewInit(): void {
    // Setup any value path inputs that exist
    this.setupValuePathInputs();
  }

  private setupValuePathInputs(): void {
    // Find all value path inputs and set them up
    // This is more complex as we have dynamic parameter forms
    setTimeout(() => {
      const paramArray = this.templateForm.get('parameters') as FormArray;
      if (paramArray) {
        paramArray.controls.forEach((control, index) => {
          if ((control as FormGroup).get('kind')?.value === ParameterKind.ConversationData) {
            this.setupValuePathPrefix(index);
          }
        });
      }

      // Listen for changes in parameter kinds to update value path inputs
      paramArray.valueChanges.subscribe(() => {
        paramArray.controls.forEach((control, index) => {
          if ((control as FormGroup).get('kind')?.value === ParameterKind.ConversationData) {
            this.setupValuePathPrefix(index);
          }
        });
      });
    }, 0);
  }

  private setupValuePathPrefix(index: number): void {
    const paramGroup = (this.templateForm.get('parameters') as FormArray).at(index) as FormGroup;
    const valuePathControl = paramGroup.get('valuePath');

    if (!valuePathControl) return;

    // Strip the prefix if it's already there
    let currentValue = valuePathControl.value || '';
    if (currentValue.startsWith(this.valuePathPrefix)) {
      currentValue = currentValue.substring(this.valuePathPrefix.length);
      valuePathControl.setValue(currentValue, { emitEvent: false });
    }
  }

  initForm(): void {
    if (this.isEditMode()) {
      // When editing, name is not editable
      this.templateForm = this.#formBuilder.group({
        description: [this.data.template?.description || ''],
        kind: [this.data.template?.kind || MessageTemplateKind.Text, [Validators.required]],
        texts: this.#formBuilder.array([]),
        parameters: this.#formBuilder.array([]),
      });
    } else {
      // When creating a new template
      this.templateForm = this.#formBuilder.group({
        name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
        description: [''],
        kind: [MessageTemplateKind.Text, [Validators.required]],
        texts: this.#formBuilder.array([]),
        parameters: this.#formBuilder.array([]),
      });
    }

    // Initialize texts form array
    const textsArray = this.templateForm.get('texts') as FormArray;
    if (this.data.template?.texts) {
      // Add existing texts
      Object.entries(this.data.template.texts).forEach(([lang, text]) => {
        textsArray.push(this.createTextGroup(lang, text));
      });
    } else {
      // Add empty text for English by default
      textsArray.push(this.createTextGroup('en', ''));
    }

    // Initialize parameters form array
    const parametersArray = this.templateForm.get('parameters') as FormArray;
    if (this.data.template?.parameters && this.data.template.parameters.length > 0) {
      // Add existing parameters
      this.data.template.parameters.forEach((param) => {
        parametersArray.push(this.createParameterGroup(param));
      });
    }
  }

  createTextGroup(language: string, text: string): FormGroup {
    return this.#formBuilder.group({
      language: [language, Validators.required],
      text: [text, Validators.required],
    });
  }

  createParameterGroup(param?: MessageTemplateParameter): FormGroup {
    let paramKind = param?.kind || ParameterKind.CurrentTimestamp;

    // If we're editing an existing parameter, always keep its original kind
    // even if it wouldn't normally be allowed in the current context
    if (!param && this.filteredParameterTypes.length > 0) {
      // For new parameters, ensure the kind is allowed in the current context
      if (!this.filteredParameterTypes.includes(paramKind)) {
        paramKind = this.filteredParameterTypes[0] || ParameterKind.CurrentTimestamp;
      }
    }

    return this.#formBuilder.group({
      name: [param?.name || '', [Validators.required, Validators.pattern(/^[a-zA-Z0-9_]+$/)]],
      kind: [paramKind, Validators.required],
      valuePath: [param?.valuePath || ''],
    });
  }

  get textsArray(): FormArray {
    return this.templateForm.get('texts') as FormArray;
  }

  get parametersArray(): FormArray {
    return this.templateForm.get('parameters') as FormArray;
  }

  addText(): void {
    // Find a language that hasn't been used yet
    const usedLanguages = this.textsArray.controls.map(
      (control) => (control as FormGroup).get('language')?.value
    );
    const availableLanguage = this.availableLanguages.find((lang) => !usedLanguages.includes(lang));

    if (availableLanguage) {
      this.textsArray.push(this.createTextGroup(availableLanguage, ''));
    }
  }

  removeText(index: number): void {
    this.textsArray.removeAt(index);
  }

  addParameter(): void {
    this.parametersArray.push(this.createParameterGroup());
  }

  removeParameter(index: number): void {
    this.parametersArray.removeAt(index);
  }

  shouldShowValuePath(control: AbstractControl): boolean {
    const kind = control.get('kind')?.value;
    return kind === ParameterKind.ConversationData;
  }

  closeDialog(save: boolean): void {
    if (this.isConfirmation()) {
      this.#dialogRef.close(save);
      return;
    }

    if (!save) {
      this.#dialogRef.close(null);
      return;
    }

    if (this.templateForm.valid) {
      // Process form data for API consumption
      const formValue = { ...this.templateForm.value };

      // Convert texts array to map of language -> text
      const textsMap: Record<string, string> = {};
      formValue.texts.forEach((item: { language: string; text: string }) => {
        textsMap[item.language] = item.text;
      });

      // Process parameters to include full value paths for ConversationData params
      if (formValue.parameters && formValue.parameters.length > 0) {
        formValue.parameters = formValue.parameters.map((param: any) => {
          if (param.kind === ParameterKind.ConversationData && param.valuePath) {
            // Ensure the prefix is added
            const valuePath = param.valuePath.startsWith(this.valuePathPrefix)
              ? param.valuePath
              : this.valuePathPrefix + param.valuePath;

            return {
              ...param,
              valuePath,
            };
          }
          return param;
        });
      }

      // Build response object
      const result = {
        ...formValue,
        texts: textsMap,
      };

      this.#dialogRef.close(result);
    } else {
      // Mark all fields as touched to show validation errors
      this.templateForm.markAllAsTouched();
    }
  }

  isLanguageDisabled(language: string, currentIndex: number): boolean {
    return this.textsArray.controls.some((control, index) => {
      if (index === currentIndex) return false;
      return (control as FormGroup).get('language')?.value === language;
    });
  }

  get templateKind() {
    return this.templateForm.get('kind') as FormControl<string>;
  }
}
